import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SprayCan, Wrench, Leaf, Truck, Droplets, Plug, PaintBucket, Snowflake, HardHat, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

const SERVICE_CATEGORIES = {
  'Pulizie': ['Pulizie domestiche', 'Impresa di pulizia', 'Pulizia moquette e tappeti', 'Sgombero ingombranti'],
  'Tuttofare': ['Tuttofare', 'Montaggio mobili', 'Riparazione elettrodomestici', 'Installazione e riparazione serrature'],
  'Giardinieri': ['Giardiniere', 'Taglio e cura del prato', 'Potatura e abbattimento alberi', 'Impianti di irrigazione'],
  'Traslochi': ['Ditta traslochi', 'Imballaggio e disimballaggio', 'Spostamento mobili pesanti', 'Trasporto pianoforti'],
  'Idraulici': ['Idraulico / Termoidraulico', 'Riparazione scaldabagno', 'Riparazione tubature', 'Spurgo e fognature'],
  'Elettricisti': ['Elettricista', 'Installazione prese e interruttori', 'Impianto domotica', 'Impianto fotovoltaico'],
  'Imbianchini': ['Imbianchino', 'Tappezziere', 'Tende e oscuranti', 'Verniciatura recinzioni'],
  'Climatizzazione': ['Climatizzazione', 'Riparazione caldaia', 'Pulizia condotti e ventilazione', 'Pulizia e riparazione camino'],
  'Edilizia': ['Ristrutturazioni', 'Muratore', 'Cartongessista', 'Riparazione tetti'],
  'Ascensoristi': ['Ascensoristi', 'Manutenzione ascensori', 'Installazione piattaforme elevatrici', 'Riparazione e collaudo ascensori'],
};

const TAB_ICONS = {
  'Pulizie': SprayCan,
  'Tuttofare': Wrench,
  'Giardinieri': Leaf,
  'Traslochi': Truck,
  'Idraulici': Droplets,
  'Elettricisti': Plug,
  'Imbianchini': PaintBucket,
  'Climatizzazione': Snowflake,
  'Edilizia': HardHat,
  'Ascensoristi': Building2,
};

const TAB_LABELS = Object.keys(SERVICE_CATEGORIES);

const SUBCATEGORY_IMAGES = {
  'Pulizie domestiche': '/assets/images/pulizie_domestiche.png',
  'Impresa di pulizia': '/assets/images/impresa_pulizia.png',
  'Pulizia moquette e tappeti': '/assets/images/pulizia_moquette_tappeti.png',
  'Sgombero ingombranti': '/assets/images/sgombero_ingombranti.png',
  'Tuttofare': '/assets/images/tuttofare_handyman.png',
  'Montaggio mobili': '/assets/images/montaggio_mobili.png',
  'Riparazione elettrodomestici': '/assets/images/riparazione_elettrodomestici.png',
  'Installazione e riparazione serrature': '/assets/images/installazione_riparazione_serrature.png',
  'Giardiniere': '/assets/images/giardiniere_pruning.png',
  'Taglio e cura del prato': '/assets/images/taglio_cura_prato.png',
  'Potatura e abbattimento alberi': '/assets/images/potatura_abbattimento_alberi.png',
  'Impianti di irrigazione': '/assets/images/impianti_irrigazione.png',
  'Ditta traslochi': '/assets/images/traslochi_ditta_traslochi.png',
  'Imballaggio e disimballaggio': '/assets/images/traslochi_imballaggio_disimballaggio.png',
  'Spostamento mobili pesanti': '/assets/images/traslochi_spostamento_mobili_pesanti.png',
  'Trasporto pianoforti': '/assets/images/traslochi_trasporto_pianoforti.png',
  'Idraulico / Termoidraulico': '/assets/images/idraulico_termoidraulico.png',
  'Riparazione scaldabagno': '/assets/images/riparazione_scaldabagno.png',
  'Riparazione tubature': '/assets/images/riparazione_tubature.png',
  'Spurgo e fognature': '/assets/images/spurgo_fognature.png',
  'Elettricista': '/assets/images/elettricista_voltage_tester.png',
  'Installazione prese e interruttori': '/assets/images/installazione_prese_interruttori.png',
  'Impianto domotica': '/assets/images/impianto_domotica_smart_wiring.png',
  'Impianto fotovoltaico': '/assets/images/impianto_fotovoltaico_solar_panel.png',
  'Imbianchino': '/assets/images/imbianchino_paint_roller.png',
  'Tappezziere': '/assets/images/tappezziere_wallpaper_smoothing.png',
  'Tende e oscuranti': '/assets/images/tende_oscuranti_curtain_rail.png',
  'Verniciatura recinzioni': '/assets/images/verniciatura_recinzioni_fence_brush.png',
  'Climatizzazione': '/assets/images/climatizzazione_ac_installation.png',
  'Riparazione caldaia': '/assets/images/riparazione_caldaia_boiler_wrench.png',
  'Pulizia condotti e ventilazione': '/assets/images/pulizia_condotte_ventilazione_vent_brush.png',
  'Pulizia e riparazione camino': '/assets/images/pulizia_riparazione_camino_chimney_brush.png',
  'Ristrutturazioni': '/assets/images/ristrutturazioni_blueprints_renovation_v2.png',
  'Muratore': '/assets/images/muratore_brick_mortar_trowel_v2.png',
  'Cartongessista': '/assets/images/cartongessista_drywall_screw_metal_stud_v2.png',
  'Riparazione tetti': '/assets/images/riparazione_tetti_roof_tiles_gloves_v2.png',
  'Ascensoristi': '/assets/images/ascensoristi_elevator_technician.png',
  'Manutenzione ascensori': '/assets/images/manutenzione_ascensori_control_panel_gloves.png',
  'Installazione piattaforme elevatrici': '/assets/images/installazione_piattaforme_elevatrici_home_elevator_glass.png',
  'Riparazione e collaudo ascensori': '/assets/images/riparazione_collaudo_ascensori_multimeter_machinery.png',
};

const CategoriesGridSection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Pulizie');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const tabScrollRef = useRef(null);
  const firstTab = TAB_LABELS?.[0];

  const checkScrollState = () => {
    const el = tabScrollRef?.current;
    if (!el) return;
    setCanScrollLeft(el?.scrollLeft > 4);
    setCanScrollRight(el?.scrollLeft + el?.clientWidth < el?.scrollWidth - 4);
  };

  useEffect(() => { checkScrollState(); }, []);

  useEffect(() => {
    const preloadOtherTabs = () => {
      TAB_LABELS?.forEach((label) => {
        SERVICE_CATEGORIES?.[label]?.forEach((subcategory) => {
          const src = SUBCATEGORY_IMAGES?.[subcategory];
          if (src) {
            const img = new Image();
            img.src = src;
          }
        });
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preloadOtherTabs);
    } else {
      setTimeout(preloadOtherTabs, 100);
    }
  }, []);

  const handleTabPrev = () => tabScrollRef?.current?.scrollBy({ left: -200, behavior: 'smooth' });
  const handleTabNext = () => tabScrollRef?.current?.scrollBy({ left: 200, behavior: 'smooth' });

  const handleCardClick = (subcategory) => {
    navigate('/cliente/dashboard', { state: { selectedProfession: { name: subcategory } } });
  };

  return (
    <section className="w-full bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-8">

        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-[#013776]">Servizi per ogni necessità</h2>
          <p className="text-base md:text-lg text-gray-600 mt-3">Scegli la categoria di tuo interesse</p>
        </div>

        <div className="w-full flex justify-center">
          <div className="relative border-b border-gray-200 max-w-7xl w-full">
            {canScrollLeft && (
              <button
                onClick={handleTabPrev}
                aria-label="Scorri a sinistra"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white border border-gray-200 rounded-full cursor-pointer"
                style={{ width: 32, height: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
              >
                <ChevronLeft size={16} color="#6b7280" />
              </button>
            )}
            <div
              ref={tabScrollRef}
              className="flex overflow-x-auto touch-pan-x md:justify-center"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                paddingLeft: canScrollLeft ? '40px' : '0px',
                paddingRight: canScrollRight ? '40px' : '0px',
                transition: 'padding 0.2s',
              }}
              onScroll={checkScrollState}
            >
              {TAB_LABELS?.map((label) => {
                const TabIcon = TAB_ICONS?.[label];
                const isActive = activeTab === label;
                return (
                  <button
                    key={label}
                    onClick={() => setActiveTab(label)}
                    className="flex flex-col items-center flex-shrink-0 bg-white border-none cursor-pointer transition-colors"
                    style={{
                      padding: '12px 20px',
                      borderBottom: isActive ? '2px solid #2780b5' : '2px solid transparent',
                      marginBottom: '-1px',
                    }}
                  >
                    <TabIcon size={24} color={isActive ? '#2780b5' : '#9ca3af'} style={{ marginBottom: 4 }} />
                    <span className="text-sm font-medium whitespace-nowrap" style={{ color: isActive ? '#2780b5' : '#6b7280' }}>{label}</span>
                  </button>
                );
              })}
            </div>
            {canScrollRight && (
              <button
                onClick={handleTabNext}
                aria-label="Scorri a destra"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white border border-gray-200 rounded-full cursor-pointer"
                style={{ width: 32, height: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
              >
                <ChevronRight size={16} color="#6b7280" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-8">
          <div className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2 sm:gap-5 sm:px-0 md:grid-cols-4 md:gap-4 md:px-0">
            {SERVICE_CATEGORIES?.[activeTab]?.map((subcategory, index) => {
              const cardImageSrc = SUBCATEGORY_IMAGES?.[subcategory] || null;
              const optimizedImageSrc = cardImageSrc ? `${cardImageSrc}?width=800&quality=75&format=webp` : null;
              const isFirstTab = activeTab === firstTab;
              const isEager = isFirstTab && index < 4;
              return (
                <div
                  key={subcategory}
                  onClick={() => handleCardClick(subcategory)}
                  className="relative cursor-pointer overflow-hidden hover:shadow-xl transition-shadow"
                  style={{ borderRadius: 16, aspectRatio: '16/9', minHeight: 0 }}
                >
                  {optimizedImageSrc ? (
                    <img
                      src={optimizedImageSrc}
                      alt={subcategory}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading={isEager ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchpriority={isEager ? 'high' : 'low'}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = cardImageSrc; }}
                    />
                  ) : (
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #2780b5 0%, #013776 100%)' }} />
                  )}
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.10) 60%, transparent 100%)' }} />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-white font-bold text-lg leading-tight">{subcategory}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default CategoriesGridSection;
